import { type ReactNode, useMemo } from "react";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { calculateBoldWeight, parseFontWeight } from "./constants/fontWeights";
import { mergeTypographyStyles } from "./hooks/typographyTypes";
import { useBlockStyleOverrides } from "./hooks/useBlockStyleOverrides";
import { useEditorTypography } from "./hooks/useEditorTypography";

interface ThemeVariablesContainerProps {
  children: ReactNode;
  className?: string;
}

const formatCSSVariable = (varName: string): string =>
  varName.startsWith("--") ? varName : `--${varName}`;

export function ThemeVariablesContainer({
  children,
  className,
}: Readonly<ThemeVariablesContainerProps>) {
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);
  const themeTypography = useEditorTypography();
  const blockOverrides = useBlockStyleOverrides();

  const cssVariables = useMemo(() => {
    const variables: Record<string, string> = {};

    if (!currentCanvasTheme?.cssVariables) {
      return variables;
    }

    const cssVars = currentCanvasTheme.cssVariables;

    Object.keys(cssVars).forEach(category => {
      const categoryVars = cssVars[category];
      if (categoryVars && typeof categoryVars === "object") {
        Object.entries(categoryVars).forEach(([key, value]) => {
          if (value != null) {
            variables[formatCSSVariable(key)] = String(value);
          }
        });
      }
    });

    return variables;
  }, [currentCanvasTheme]);

  // Merge typography styles: block overrides > theme defaults
  const combinedStyles = useMemo(() => {
    const typography = mergeTypographyStyles(blockOverrides, themeTypography);
    const baseFontWeight = parseFontWeight(typography.fontWeight);
    const boldFontWeight = calculateBoldWeight(baseFontWeight);

    return {
      ...cssVariables,
      ...typography,
      "--bold-font-weight": boldFontWeight,
    } as React.CSSProperties;
  }, [cssVariables, blockOverrides, themeTypography]);

  return (
    <div className={className} style={combinedStyles}>
      <style>{`
        .ProseMirror strong,
        .ProseMirror b {
          font-family: inherit !important;
          font-weight: var(--bold-font-weight);
        }

        .ProseMirror em,
        .ProseMirror i {
          font-family: inherit !important;
          font-style: italic;
        }

        .ProseMirror strong em,
        .ProseMirror strong i,
        .ProseMirror b em,
        .ProseMirror b i {
          font-family: inherit !important;
          font-weight: var(--bold-font-weight);
          font-style: italic;
        }   
      `}</style>
      {children}
    </div>
  );
}
